import { Course } from '@/types/courseTypes';

export const googleGeminiCourse: Course = {
  id: 'google-gemini-development',
  title: 'Google Gemini AI Development',
  description: 'Master multimodal AI development with Google Gemini, from API fundamentals to production applications',
  level: 'intermediate',
  duration: '16 hours',
  durationHours: 16,
  image: '/placeholder.svg',
  tags: ['Google Gemini', 'Multimodal AI', 'LLM', 'AI API', 'Generative AI'],
  prerequisites: ['JavaScript/Python basics', 'REST API concepts', 'Basic AI/ML understanding'],
  learningOutcomes: [
    'Build applications with Gemini Pro, Flash, and Ultra models',
    'Process text, images, video, and audio with multimodal AI',
    'Implement function calling and structured outputs',
    'Deploy production AI apps with Google Cloud integration',
    'Optimize costs and performance across Gemini models'
  ],
  type: 'specialized-ai',
  modules: [
    {
      id: 'gemini-module-1',
      title: 'Gemini API Fundamentals',
      description: 'Get started with Google Gemini models and API basics',
      content: `# Gemini API Fundamentals

## Gemini Model Family

### Model Variants
- **Gemini 2.5 Pro**: Most capable, complex reasoning, 2M token context
- **Gemini 2.5 Flash**: Fast, cost-effective, balanced performance
- **Gemini 2.5 Flash-Lite**: Fastest, lowest cost, simple tasks
- **Gemini Flash Image Preview**: Image generation from text

### API Setup
\`\`\`javascript
import { GoogleGenerativeAI } from "@google/generative-ai";

const genAI = new GoogleGenerativeAI(process.env.GEMINI_API_KEY);
const model = genAI.getGenerativeModel({ model: "gemini-2.5-flash" });

const result = await model.generateContent("Explain quantum computing");
console.log(result.response.text());
\`\`\`

## Structured Output & JSON Mode

### JSON Mode
\`\`\`javascript
const model = genAI.getGenerativeModel({
  model: "gemini-2.5-flash",
  generationConfig: {
    responseMimeType: "application/json",
    responseSchema: {
      type: "object",
      properties: {
        name: { type: "string" },
        age: { type: "number" },
        skills: { type: "array", items: { type: "string" } }
      }
    }
  }
});
\`\`\`

## Rate Limits & Cost Optimization

### Pricing (as of 2025)
- **Gemini 2.5 Flash**: $0.075/1M input, $0.30/1M output
- **Gemini 2.5 Pro**: $1.25/1M input, $5.00/1M output
- **Free tier**: 1,500 requests/day for Flash

### Best Practices
- Use Flash for most tasks, Pro for complex reasoning
- Batch requests when possible
- Implement caching for repeated contexts
- Monitor usage with Google Cloud Console`,
      duration: '3 hours',
      quiz: {
        questions: [
          {
            id: 'gem1-q1',
            question: 'Which Gemini model offers the best balance of speed and cost for most applications?',
            options: ['Gemini 2.5 Pro', 'Gemini 2.5 Flash', 'Gemini 2.5 Flash-Lite', 'Gemini Ultra'],
            correctAnswer: 1,
            explanation: 'Gemini 2.5 Flash provides excellent performance at a lower cost than Pro, suitable for most use cases.'
          }
        ]
      }
    },
    {
      id: 'gemini-module-2',
      title: 'Multimodal AI with Gemini',
      description: 'Process images, video, and audio with Gemini',
      content: `# Multimodal AI with Gemini

## Image Understanding
\`\`\`javascript
import fs from 'fs';

const imageData = fs.readFileSync('image.jpg');
const imagePart = {
  inlineData: {
    data: imageData.toString('base64'),
    mimeType: 'image/jpeg'
  }
};

const result = await model.generateContent([
  "What's in this image?",
  imagePart
]);
\`\`\`

## Video Processing
\`\`\`javascript
const videoFile = await fileManager.uploadFile('video.mp4', {
  mimeType: 'video/mp4',
  displayName: 'Sample Video'
});

const result = await model.generateContent([
  { text: "Summarize this video" },
  { fileData: { mimeType: videoFile.mimeType, fileUri: videoFile.uri } }
]);
\`\`\`

## PDF & Document Analysis
\`\`\`javascript
const pdfPart = {
  inlineData: {
    data: fs.readFileSync('document.pdf').toString('base64'),
    mimeType: 'application/pdf'
  }
};

const result = await model.generateContent([
  "Extract key information from this contract",
  pdfPart
]);
\`\`\``,
      duration: '4 hours',
      quiz: {
        questions: [
          {
            id: 'gem2-q1',
            question: 'What is the maximum context window size for Gemini 2.5 Pro?',
            options: ['128K tokens', '1M tokens', '2M tokens', '500K tokens'],
            correctAnswer: 2,
            explanation: 'Gemini 2.5 Pro supports up to 2 million tokens, enabling processing of very large documents.'
          }
        ]
      }
    },
    {
      id: 'gemini-module-3',
      title: 'Function Calling & Tool Use',
      description: 'Implement function calling for dynamic AI applications',
      content: `# Function Calling & Tool Use

## Function Declarations
\`\`\`javascript
const functions = [{
  name: "get_weather",
  description: "Get current weather for a location",
  parameters: {
    type: "object",
    properties: {
      location: { type: "string" },
      unit: { type: "string", enum: ["celsius", "fahrenheit"] }
    },
    required: ["location"]
  }
}];

const model = genAI.getGenerativeModel({
  model: "gemini-2.5-flash",
  tools: [{ functionDeclarations: functions }]
});
\`\`\`

## Handling Function Calls
\`\`\`javascript
const chat = model.startChat();
const result = await chat.sendMessage("What's the weather in Tokyo?");

const call = result.response.functionCalls()[0];
if (call.name === "get_weather") {
  const weatherData = await getWeather(call.args.location);
  
  const response = await chat.sendMessage([{
    functionResponse: {
      name: "get_weather",
      response: weatherData
    }
  }]);
}
\`\`\``,
      duration: '4 hours',
      quiz: {
        questions: [
          {
            id: 'gem3-q1',
            question: 'What is the purpose of function calling in Gemini?',
            options: [
              'To train custom models',
              'To enable AI to use external tools and APIs',
              'To improve response speed',
              'To reduce costs'
            ],
            correctAnswer: 1,
            explanation: 'Function calling allows Gemini to interact with external systems, databases, and APIs dynamically.'
          }
        ]
      }
    },
    {
      id: 'gemini-module-4',
      title: 'Production Apps with Gemini',
      description: 'Build and deploy production-ready AI applications',
      content: `# Production Apps with Gemini

## AI Chatbot Implementation
\`\`\`javascript
const chat = model.startChat({
  history: [
    { role: "user", parts: [{ text: "Hello" }] },
    { role: "model", parts: [{ text: "Hi! How can I help?" }] }
  ],
  generationConfig: {
    maxOutputTokens: 1000,
    temperature: 0.7
  }
});

const result = await chat.sendMessage(userMessage);
\`\`\`

## Vertex AI Integration
\`\`\`javascript
import { VertexAI } from '@google-cloud/vertexai';

const vertex = new VertexAI({
  project: 'my-project',
  location: 'us-central1'
});

const model = vertex.getGenerativeModel({ model: 'gemini-2.5-pro' });
\`\`\`

## Error Handling & Retries
\`\`\`javascript
async function generateWithRetry(prompt, maxRetries = 3) {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await model.generateContent(prompt);
    } catch (error) {
      if (error.status === 429) {
        await sleep(Math.pow(2, i) * 1000);
      } else {
        throw error;
      }
    }
  }
}
\`\`\``,
      duration: '5 hours',
      quiz: {
        questions: [
          {
            id: 'gem4-q1',
            question: 'What is Vertex AI?',
            options: [
              'A JavaScript library',
              'Google Cloud\'s managed ML platform',
              'An image generation model',
              'A database service'
            ],
            correctAnswer: 1,
            explanation: 'Vertex AI is Google Cloud\'s unified ML platform for deploying and managing AI models at scale.'
          }
        ]
      }
    }
  ]
};
